import { addDays, parseISO, formatISO } from 'date-fns';

import type { ChecklistTemplate } from '@/types/checklist-template';
import type { Season } from '@/types/season';
import type { Property } from '@/types/property';
import type { Task, CreateTaskInput } from '@/types/task';
import { TimingAnchor } from '@/types/task';
import type { TaskService } from './task-service';
import { ZONE_CONFIG, getZoneConfig, computeZoneWindowOffset } from '@/data/zone-data';
import { validateDependencies } from './dependency-utils';

// Northern Midwest is the template authoring zone — all dates are defined relative to it
const NORTHERN_ZONE = ZONE_CONFIG['northern_midwest'];

/**
 * Resolve a calendar-date task, optionally shifting by zone offset.
 * Zone offset is computed from the fallBrassicaWindow — the broadest food-plot window
 * and the standard reference for zone-adjusted tasks.
 */
function resolveCalendarDate(
  month: number,
  day: number,
  year: number,
  zone: string | undefined,
  zoneAdjustable: boolean,
): string {
  const baseDate = new Date(year, month - 1, day);
  if (!zoneAdjustable || !zone || zone === 'northern_midwest') {
    return formatISO(baseDate, { representation: 'date' });
  }
  const targetConfig = getZoneConfig(zone);
  if (!targetConfig) {
    return formatISO(baseDate, { representation: 'date' });
  }
  const offsetDays = computeZoneWindowOffset(
    NORTHERN_ZONE.fallBrassicaWindow,
    targetConfig.fallBrassicaWindow,
  );
  return formatISO(addDays(baseDate, offsetDays), { representation: 'date' });
}

/**
 * Resolve a frost-relative task.
 * Negative offsetDays = before average first frost; positive = after average first frost.
 */
function resolveFrostDate(zone: string | undefined, offsetDays: number, year: number): string {
  const config = zone ? getZoneConfig(zone) : null;
  const frostMonth = config?.averageFirstFrostMonth ?? NORTHERN_ZONE.averageFirstFrostMonth;
  const frostDay = config?.averageFirstFrostDay ?? NORTHERN_ZONE.averageFirstFrostDay;
  const frostDate = new Date(year, frostMonth - 1, frostDay);
  return formatISO(addDays(frostDate, offsetDays), { representation: 'date' });
}

/**
 * Resolve a rut-relative task.
 * offsetDays = 0 means rut peak day; negative = before peak; positive = after peak.
 */
function resolveRutRelativeDate(zone: string | undefined, offsetDays: number, year: number): string {
  const config = zone ? getZoneConfig(zone) : null;
  const rutMonth = config?.rutPeakMonth ?? NORTHERN_ZONE.rutPeakMonth;
  const rutDay = config?.rutPeakDay ?? NORTHERN_ZONE.rutPeakDay;
  const rutDate = new Date(year, rutMonth - 1, rutDay);
  return formatISO(addDays(rutDate, offsetDays), { representation: 'date' });
}

/**
 * ChecklistEngine generates concrete Tasks from a ChecklistTemplate + Season + optional Property.
 *
 * Zone-aware: calendar_date tasks with zoneAdjustable=true shift by the difference between
 * the property's fallBrassicaWindow and the Northern Midwest reference window.
 */
export class ChecklistEngine {
  constructor(private taskService: TaskService) {}

  async generateFromTemplate(
    template: ChecklistTemplate,
    season: Season,
    property?: Property,
  ): Promise<Task[]> {
    const openerDate = parseISO(season.openerDate);
    const year = openerDate.getFullYear();
    const zone = property?.zone;

    // Validate dependency graph before allocating any IDs
    const depValidation = validateDependencies(
      template.tasks.map((t) => ({ id: t.id, dependsOn: t.dependsOn })),
    );
    if (!depValidation.valid) {
      throw new Error(
        `Circular dependency in template "${template.name}": ${depValidation.cycle?.join(' → ')}`,
      );
    }

    // Pre-assign stable UUIDs so dependency chains can reference sibling tasks
    const idMap = new Map<string, string>();
    template.tasks.forEach((tt) => idMap.set(tt.id, crypto.randomUUID()));

    const inputs: CreateTaskInput[] = template.tasks.map((tt, index) => {
      const generatedId = idMap.get(tt.id)!; // SAFETY: idMap is populated for every task above

      let dueDate: string;
      switch (tt.timingAnchor) {
        case TimingAnchor.CalendarDate:
          dueDate = resolveCalendarDate(
            tt.calendarMonth ?? 1,
            tt.calendarDayRangeStart ?? 1,
            year,
            zone,
            tt.zoneAdjustable,
          );
          break;
        case TimingAnchor.FrostDate:
          dueDate = resolveFrostDate(zone, tt.offsetDays, year);
          break;
        case TimingAnchor.RutRelative:
          dueDate = resolveRutRelativeDate(zone, tt.offsetDays, year);
          break;
        case TimingAnchor.OpenerRelative:
        default:
          dueDate = formatISO(addDays(openerDate, tt.offsetDays), { representation: 'date' });
          break;
      }

      const dependsOn = tt.dependsOn
        .map((depId) => idMap.get(depId))
        .filter((d): d is string => d !== undefined);

      return {
        id: generatedId,
        templateTaskId: tt.id,
        seasonId: season.id,
        propertyId: tt.propertyTied ? property?.id : undefined,
        title: tt.title,
        description: tt.description,
        category: tt.category,
        selectedAction: '',
        dueDate,
        timingAnchor: tt.timingAnchor,
        offsetDays: tt.offsetDays,
        dependsOn,
        blocked: dependsOn.length > 0,
        completed: false,
        sortOrder: tt.sortOrder ?? index,
        notes: tt.notes ?? '',
      };
    });

    return this.taskService.bulkCreateTasks(inputs);
  }
}
